import { FunctionComponent, useState, useEffect, useMemo } from 'react'
import DashboardLayout from '../../components/layout/layout'
import { Alert, Box, Button, Checkbox, FormControlLabel, FormGroup, Grid, MenuItem, Skeleton, styled, Typography } from '@mui/material'
import { useNavigate,useLocation } from 'react-router-dom'
import { StyledSelect, StyledTextField } from '../../components/ui/formInput'
import { useGetChannelPartnerOEntryHeader } from '../../hooks/api/oms'
import StorageService from '../../services/storageService'

const CheckboxUI = styled(Checkbox)(({ theme }) => ({
  '&.Mui-checked': {
    color: '#c3183c',
  },
}));

export const ACTIVE_COLOR = "#c3183c";
export const INACTIVE_COLOR = "rgba(0, 0, 0, 0.6)";

export interface OEntryHeader {
  customerBlockedDay: string;
  orderOptions: OrderOption;
  shipTermsOptions: ShipTerm[];
  customerConsolidationDay: string;
  customerNumber: string;
  customerComments: string[];
  billToInfo: Address;
  success: boolean;
  consolidationDays: ConsolidationDay[];
  shipViaOptions: ShipViaOption[];
  customerDefaults: CustomerDefaults;
  blockedDays: BlockedDay[];
  shipToAddresses: Address[];
}

export interface OrderOption {
  description: string;
  code: string;
}

export interface ShipTerm {
  ft_terms: string;
  ft_desc: string;
}

export interface Address {
  ad_addr: string;
  ad_name: string;
  ad_line1: string;
  ad_line2: string;
  ad_line3: string;
  ad_city: string;
  ad_state: string;
  ad_zip: string;
  ad_phone: string;
  ad_fax: string;
  defaultCarrierAccount: string;
}

export interface ConsolidationDay {
  cpnum: number;
  cpname: string;
  condayname: string;
  conday: number;
}

export interface ShipViaOption {
  fr_list: string;
  fr_desc: string;
}

export interface CustomerDefaults {
  cm_fr_list: string;
  cm_fr_terms: string;
  cm_rmks: string;
  cm_cr_hold: boolean;
  cm_cr_limit: number;
  cm_cr_terms: string;
  cm_balance: number;
  cm_class: string;
}

export interface BlockedDay {
  cpnum: number;
  cpname: string;
  blockdayname: string;
  blockday: number;
}

type ValidateField = {
  invalid: boolean;
  error: string;
};

type ValidateFieldsState = {
  shipTo: ValidateField;
  shipViaCarrier: ValidateField;
  shippingTerms: ValidateField;
  carrierAccountNo: ValidateField;
  purchaseOrderNumber: ValidateField;
  otherComments: ValidateField;
  selectDay: ValidateField;
};

const OnlineOrderEntryHeader: FunctionComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const custNum = queryParams.get('custNum');
  useEffect(() => {
    if (custNum) {
      StorageService.setOEntryAdminCustomer(custNum);
    }
    
    // Clear any existing order entry data when starting a new order
    // This ensures the form starts fresh when user navigates to order entry
    // BUT preserve the selected customer number for on-behalf users
    const clearExistingData = () => {
      // Store the customer number before clearing
      const selectedCustNum = StorageService.getOEntryAdminCustomer();
      
      // Clear cart data but preserve customer number
      StorageService.clearCart();
      localStorage.removeItem('cart_ship_dates');
      localStorage.removeItem('cart_comments');
      
      // Restore the customer number after clearing
      if (selectedCustNum && selectedCustNum.trim() !== '') {
        StorageService.setOEntryAdminCustomer(selectedCustNum);
      }
    };
    
    clearExistingData();
  }, [custNum]);

  const [disabled, setDisabled] = useState<boolean>(false);
  const [error, setError] = useState('');
  const oEntryHeaderValue = StorageService.getOEntryHeaderValue();
  // Get current user info to check access level
  const currentUser = StorageService.getUserInfo();
  const isCPUser = currentUser?.accessLevel === 'CP';
  
  // Pass custNum if available (from URL or storage) - backend will validate based on on-behalf status
  // For on-behalf users (CP or Barksdale), backend will use the requested custNum
  // For non-on-behalf users, backend will use their assigned customer number
  const oEntryAdminCustomer = custNum || StorageService.getOEntryAdminCustomer();
  const { data: oentryheader, isFetched: hasFetched, isLoading } = useGetChannelPartnerOEntryHeader(oEntryAdminCustomer);
  const [shipTo, setShipTo] = useState(oEntryHeaderValue?.shipTo || '');
  const [shipViaCarrier, setShipViaCarrier] = useState(oEntryHeaderValue?.shipViaCarrier ||'');
  const [shippingTerms, setShippingTerms] = useState(oEntryHeaderValue?.shippingTerms || '');
  const [carrierAccountNo, setCarrierAccountNo] = useState(oEntryHeaderValue?.carrierAccountNo || '');
  const [purchaseOrderNumber, setPurchaseOrderNumber] = useState(oEntryHeaderValue?.purchaseOrderNumber || '');
  const setDefaultComment = `** Enter comments here if not listed in standard comments above **\n**         Please do not enter unnecessary comments.         **`;
  const [otherComments, setOtherComments] = useState(oEntryHeaderValue?.otherComments || "");
  const [selectDay, setSelectDay] = useState(oEntryHeaderValue?.selectDay || '');

  const [uniqueConDays, setUniqueConDays] = useState<string[]>([]);
  // const [isSelectDayRequired, setIsSelectDayRequired] = useState(false);

  const [comments, setComments] = useState({
    oe: oEntryHeaderValue?.comments?.oe ?? false,
    doNotPartial: oEntryHeaderValue?.comments?.doNotPartial ?? false,
    expedite: oEntryHeaderValue?.comments?.expedite ?? false,
    con: oEntryHeaderValue?.comments?.con ?? (oEntryHeaderValue?.comments?.selectDay ?? false),
  });

  useEffect(() => {
    if (
      hasFetched &&
      oentryheader &&
      oentryheader.success &&
      oentryheader.shipToAddresses?.length > 0 &&
      oentryheader.shipViaOptions?.length > 0 &&
      oentryheader.shipTermsOptions?.length > 0 &&
      !oEntryHeaderValue?.shipTo
    ) {
      if (oentryheader.shipToAddresses.length === 1) {
        // Single address - auto-select it
        setShipTo(oentryheader.shipToAddresses[0].ad_addr);
      } else if (oentryheader.shipToAddresses.length > 1) {
        // Multiple addresses - show "Select Ship To" (empty value)
        setShipTo('');
      }
    }
  }, [hasFetched, oEntryHeaderValue?.shipTo, oentryheader]);

  // Auto-populate carrier account when shipping terms change to "COLLECT"
  useEffect(() => {
    if (shippingTerms === 'COLLECT' && shipTo && oentryheader?.shipToAddresses) {
      const selectedAddress = oentryheader.shipToAddresses.find((addr: Address) => addr.ad_addr === shipTo);
      if (selectedAddress?.defaultCarrierAccount && !carrierAccountNo) {
        setCarrierAccountNo(selectedAddress.defaultCarrierAccount);
      }
    }
  }, [shippingTerms, shipTo, oentryheader?.shipToAddresses, carrierAccountNo]);

  useEffect(() => {
    if (
      hasFetched &&
      oentryheader &&
      oentryheader.success &&
      oentryheader.shipToAddresses?.length > 0 &&
      oentryheader.shipViaOptions?.length > 0 &&
      oentryheader.shipTermsOptions?.length > 0
    ) {
      if (oentryheader?.consolidationDays?.length) {
        const uniqueNames: string[] = Array.from(
          new Set(oentryheader.consolidationDays.map((day: ConsolidationDay) => day.condayname))
        );
        setUniqueConDays(uniqueNames);
      }
      
      // Auto-initialize CON checkbox and day selection (matches legacy ASP logic)
      // Legacy: If PONum is empty (first visit) AND customer has consolidation day configured
      if (!purchaseOrderNumber && oentryheader?.customerConsolidationDay?.condayname) {
        setComments(prev => ({ ...prev, con: true }));
        setSelectDay(oentryheader.customerConsolidationDay.condayname);
      }

      // Pre-populate Ship Via Carrier from customer defaults if not already set
      if (!shipViaCarrier && oentryheader?.customerDefaults?.cm_fr_list) {
        setShipViaCarrier(oentryheader.customerDefaults.cm_fr_list);
      }

      // Pre-populate Shipping Terms from customer defaults if not already set
      if (!shippingTerms && oentryheader?.customerDefaults?.cm_fr_terms) {
        setShippingTerms(oentryheader.customerDefaults.cm_fr_terms);
      }

      // Pre-populate Carrier Account if Shipping Terms is COLLECT and Ship To is selected
      if (shippingTerms === 'COLLECT' && shipTo && oentryheader?.shipToAddresses && !carrierAccountNo) {
        const selectedAddress = oentryheader.shipToAddresses.find((addr: Address) => addr.ad_addr === shipTo);
        if (selectedAddress?.defaultCarrierAccount) {
          setCarrierAccountNo(selectedAddress.defaultCarrierAccount);
        }
      }
    }
  }, [hasFetched, oEntryHeaderValue?.shipTo, oentryheader, purchaseOrderNumber, shipViaCarrier, shippingTerms, shipTo, carrierAccountNo]);

  const handleOtherCommentsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOtherComments(e.target.value);
  };

  const formValidation: ValidateFieldsState = {
    shipTo: { invalid: false, error: '' },
    shipViaCarrier: { invalid: false, error: '' },
    shippingTerms: { invalid: false, error: '' },
    carrierAccountNo: { invalid: false, error: '' },
    purchaseOrderNumber: { invalid: false, error: '' },
    otherComments: { invalid: false, error: '' },
    selectDay: { invalid: false, error: '' },
  };
  const [validateFields, setValidateFields] = useState<ValidateFieldsState>(formValidation);

  const validateForm = () => {
    let valid = true;
    const errors = { ...validateFields };

    if (!shipTo) {
      errors.shipTo = { invalid: true, error: 'Ship To is required' };
      valid = false;
    } else {
      errors.shipTo = { invalid: false, error: '' };
    }
    if (!shipViaCarrier) {
      errors.shipViaCarrier = { invalid: true, error: 'Ship Via Carrier is required' };
      valid = false;
    } else {
      errors.shipViaCarrier = { invalid: false, error: '' };
    }
    if (!shippingTerms) {
      errors.shippingTerms = { invalid: true, error: 'Shipping Terms is required' };
      valid = false;
    } else {
      errors.shippingTerms = { invalid: false, error: '' };
    }
    if (shippingTerms === 'COLLECT' && !carrierAccountNo?.trim()) {
      errors.carrierAccountNo = { invalid: true, error: 'Carrier Account No. is required' };
      valid = false;
    } else {
      errors.carrierAccountNo = { invalid: false, error: '' };
    }
    if (!purchaseOrderNumber?.trim()) {
      errors.purchaseOrderNumber = { invalid: true, error: 'Purchase Order No. is required' };
      valid = false;
    } else {
      errors.purchaseOrderNumber = { invalid: false, error: '' };
    }

    // Validate Other Comments character limit
    if (otherComments.length > 345) {
      errors.otherComments = { invalid: true, error: 'Other Comments cannot exceed 345 characters' };
      valid = false;
    } else {
      errors.otherComments = { invalid: false, error: '' };
    }

    // if (isSelectDayRequired) {
    //   if (!selectDay) {
    //     errors.selectDay = { invalid: true, error: 'Select Day is required' };
    //     valid = false;
    //   } else {
    //     errors.selectDay = { invalid: false, error: '' };
    //   }
    // }

    // New validation: Select Day is only mandatory when the checkbox is checked
    if (comments.con && !selectDay) {
      errors.selectDay = { invalid: true, error: 'Select Day is required' };
      valid = false;
    } else {
      errors.selectDay = { invalid: false, error: '' };
    }

    // if (oentryheader?.customerComments?.length === 0) {
      
    //   if (!selectDay) {
    //     errors.selectDay = { invalid: true, error: 'Select Day is required' };
    //     valid = false;
    //    } 
    //    //else if (comments?.selectDay === false || comments?.selectDay === "") {
    //   //   errors.selectDay = { invalid: true, error: 'Please select the Consolidate order' };
    //   //   valid = false;
    //   // }
    //    else {
    //     errors.selectDay = { invalid: false, error: '' };
    //   }
    // }

    setValidateFields(errors);
    return valid;
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // setIsSelectDayRequired(false)
    const { name, checked, value, type } = event.target;
    // if (name === 'selectDay') {
    //   setIsSelectDayRequired(true)
    // }
    setComments((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleNext = () => {
    setValidateFields(formValidation);
    if (!validateForm()) {
      return;
    }
    const onlineOrderEntryHeader = {
      shipTo,
      shipViaCarrier,
      shippingTerms,
      carrierAccountNo,
      purchaseOrderNumber,
      otherComments: otherComments,
      selectDay: selectDay,
      comments: {
        oe: comments.oe,
        doNotPartial: comments.doNotPartial,
        expedite: comments.expedite,
        con: comments.con
      }
    }
    StorageService.setOEntryHeader(oentryheader);
    StorageService.setOEntryHeaderValue(onlineOrderEntryHeader);
    setDisabled(true);
    setError('');
    navigate('/order-management-system/online-order-entry/order');
  };

  const handleOnBackClick = () => {
    navigate('/order-management-system');
  };

  // Memoize the customer comments section to prevent unnecessary re-renders
  const memoizedCustomerComments = useMemo(() => {
    if (!oentryheader?.customerComments?.length) {
      return null;
    }
    return oentryheader.customerComments;
  }, [oentryheader?.customerComments]);

  // Auto-check OE checkbox when customer comments are available (only once)
  useEffect(() => {
    if (memoizedCustomerComments && memoizedCustomerComments.length > 0 && !comments.oe) {
      setComments(prev => ({
        ...prev,
        oe: true
      }));
    }
  }, [memoizedCustomerComments]); // Removed comments.oe from dependencies to prevent re-triggering

  return (
    <div className="online-order-entry-page">
      <DashboardLayout title={"Orders Management"} initialBreadcrumbs={"Channel Partners"} levelBreadcrumbs={"Orders Management"} breadcrumbs={'Online Order Entry'} activeMenu={'oms'} containerTitle={
      <>
        <Typography variant="subtitle1" color="#000000" fontWeight="bold" gutterBottom>
          Sold To:
        </Typography>
        <Typography variant="subtitle1" color="#000000" fontWeight="medium" gutterBottom>
          {isLoading ? <Skeleton width="38%" /> : oentryheader?.billToInfo?.ad_name}
        </Typography>
        <Typography variant="subtitle1" color="#000000" gutterBottom sx={{ width: '30%' }}>
          {isLoading ? <Skeleton width="38%" /> : `${oentryheader?.billToInfo?.ad_line1} ${oentryheader?.billToInfo?.ad_city}, ${oentryheader?.billToInfo?.ad_state} ${oentryheader?.billToInfo?.ad_zip}`}
        </Typography>
      </>
    }>
      <Grid container spacing={2}>
                 <Grid size={12}>
           <Typography variant="subtitle1" color="#000000" fontWeight="bold" gutterBottom>
             Ship To:
           </Typography>
          <StyledSelect
            label="Select Ship To"
            value={shipTo}
            onChange={(e: any) => {
              setShipTo(e.target.value);
              // Auto-populate carrier account if shipping terms is COLLECT
              if (e.target.value && shippingTerms === 'COLLECT' && oentryheader?.shipToAddresses) {
                const selectedAddress = oentryheader.shipToAddresses.find((addr: Address) => addr.ad_addr === e.target.value);
                if (selectedAddress?.defaultCarrierAccount) {
                  setCarrierAccountNo(selectedAddress.defaultCarrierAccount);
                }
              }
            }}
            error={validateFields.shipTo.invalid}
            helperText={validateFields.shipTo.error}
          >
            <MenuItem key={'default'} value={''}>
              Ship To
            </MenuItem>
            {oentryheader?.shipToAddresses?.map((addr: Address) => (
              <MenuItem key={addr.ad_addr} value={addr.ad_addr}>
                {`${addr.ad_name} ${addr.ad_line1} ${addr.ad_city}, ${addr.ad_state} ${addr.ad_zip}`}
              </MenuItem>
            ))}

          </StyledSelect>
        </Grid>
        <Grid size={3}>
          <Typography variant="subtitle1" color="#000000" fontWeight="bold" gutterBottom>
            Ship Via Carrier:
          </Typography>
          <StyledSelect
            label="Select Carrier"
            value={shipViaCarrier}
            onChange={(e: any) => setShipViaCarrier(e.target.value)}
            error={validateFields.shipViaCarrier.invalid}
            helperText={validateFields.shipViaCarrier.error}
          >
            <MenuItem key={'default'} value={''}>
              Ship Carrier
            </MenuItem>
            {oentryheader?.shipViaOptions?.map((opt: ShipViaOption) => (
              <MenuItem key={opt.fr_list} value={opt.fr_list}>
                {opt.fr_desc}
              </MenuItem>
            ))}
          </StyledSelect>
        </Grid>
        <Grid size={3}>
            <Typography variant="subtitle1" color="#000000" fontWeight="bold" gutterBottom>
              Shipping Terms
            </Typography>
                       <StyledSelect
             label="Shipping Terms"
             value={shippingTerms}
             onChange={(e: any) => {
               setShippingTerms(e.target.value);
               // Clear carrier account if not COLLECT
               if (e.target.value !== 'COLLECT') {
                 setCarrierAccountNo('');
               }
             }}
             error={validateFields.shippingTerms.invalid}
             helperText={validateFields.shippingTerms.error}
           >
              <MenuItem key={'default'} value={''}>
                Shipping Terms
              </MenuItem>
              {oentryheader?.shipTermsOptions?.map((term: ShipTerm) => (
                <MenuItem key={term.ft_terms} value={term.ft_terms}>
                  {term.ft_desc.trim()}
                </MenuItem>
              ))}
            </StyledSelect>
        </Grid>
        {shippingTerms === 'COLLECT' && (
        <Grid size={3}>
            <Typography variant="subtitle1" color="#000000" fontWeight="bold" gutterBottom>
              Carrier Account No.
            </Typography>
            <StyledTextField
              label="Carrier Account No."
              value={carrierAccountNo}
              onChange={(e: any) => setCarrierAccountNo(e.target.value)}
              error={validateFields.carrierAccountNo.invalid}
              helperText={validateFields.carrierAccountNo.error}
            />
        </Grid>
        )}
        <Grid size={3}>
            <Typography variant="subtitle1" color="#000000" fontWeight="bold" gutterBottom>
              Purchase Order No.
            </Typography>
            <StyledTextField
              label="Purchase Order No."
              value={purchaseOrderNumber}
              onChange={(e: any) => setPurchaseOrderNumber(e.target.value.toUpperCase())}
              error={validateFields.purchaseOrderNumber.invalid}
              helperText={validateFields.purchaseOrderNumber.error}
              inputProps={{ maxLength: 20 }}
              //sx={{ maxWidth: '200px' }}  // Visual width limitation
            />
        </Grid>
        <Grid size={12}>
            <Typography variant="h6" gutterBottom>
              Standard Comments:
            </Typography>
        </Grid>
        {
          memoizedCustomerComments ? (
            <>
              {/* Add OE checkbox above existing customer comments - only show when customer has custom comments */}
              {memoizedCustomerComments && memoizedCustomerComments.length > 0 && (
                <Grid size={12}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <CheckboxUI
                          checked={comments.oe}
                          onChange={handleChange}
                          name="oe"
                        />
                      }
                      label={
                                              <Box>
                        <Typography variant="body2" color="#000000">
                          Order Entry (Standard Comments)
                        </Typography>
                      </Box>
                      }
                    />
                  </FormGroup>
                </Grid>
              )}
              {/* Customer comments display with single-space standard formatting */}
              {memoizedCustomerComments.map((comment: Record<string, string>, index: number) => (
                Array.from({ length: 15 }, (_, i) => {
                  const key = `cmt_cmmt${i + 1}`;
                  const value = comment[key];
                  return value && value.trim() !== '.' ? (
                    <Grid size={12} key={`${index}-${i}`}>
                      <Typography variant="body2" color="#000000" sx={{ mb: 0.25, lineHeight: 1.2 }}>
                        {value.replace(/\s+/g, ' ').trim()}
                      </Typography>
                    </Grid>
                  ) : null;
                })
              ))}
              {/* Add standard checkboxes below customer comments */}
              <Grid size={12}>
                <FormGroup sx={{ '& .MuiFormControlLabel-root': { marginBottom: '0px', marginTop: '0px', padding: '2px 0' } }}>
                  <FormControlLabel
                    control={
                      <CheckboxUI
                        checked={comments.doNotPartial}
                        onChange={handleChange}
                        name="doNotPartial"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" color="#000000">
                          Do not partial (Ship order when all line items are available)
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={
                      <CheckboxUI
                        checked={comments.expedite}
                        onChange={handleChange}
                        name="expedite"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" color="#000000">
                          Expedite Order/RSS (Additional charge: Greater of $100 or 20% of order)
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={
                      <CheckboxUI
                        checked={comments.con}
                        onChange={handleChange}
                        name="con"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" color="#000000" sx={{ whiteSpace: 'nowrap' }}>
                            Consolidate order on
                          </Typography>
                          <Box sx={{ minWidth: 120 }}>
                              <StyledSelect
                                label="Select Day"
                                value={selectDay}
                                onChange={(e: any) => setSelectDay(e.target.value)}
                                error={validateFields.selectDay.invalid}
                                helperText={validateFields.selectDay.error}
                              >
                            <MenuItem key="select-day" value="">
                              Select Day
                            </MenuItem>
                            {uniqueConDays.map((name: string) => (
                              <MenuItem key={name} value={name}>
                                {name}
                              </MenuItem>
                            ))}
                              </StyledSelect>
                          </Box>
                      </Box>
                    }
                />
                </FormGroup>
              </Grid>
            </>
          ) : (
            // Keep existing checkbox-only display when no customer comments (no OE checkbox when no customer comments)
            <Grid size={12}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <CheckboxUI
                      checked={comments.doNotPartial}
                      onChange={handleChange}
                      name="doNotPartial"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">
                        Do not partial (Ship order when all line items are available)
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  control={
                    <CheckboxUI
                      checked={comments.expedite}
                      onChange={handleChange}
                      name="expedite"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">
                        Expedite Order/RSS (Additional charge: Greater of $100 or 20% of order)
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  control={
                    <CheckboxUI
                      checked={comments.con}
                      onChange={handleChange}
                      name="con"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                          Consolidate order on
                        </Typography>
                        <Box sx={{ minWidth: 120 }}>
                            <StyledSelect
                              label="Select Day"
                              value={selectDay}
                              onChange={(e: any) => setSelectDay(e.target.value)}
                              error={validateFields.selectDay.invalid}
                              helperText={validateFields.selectDay.error}
                            >
                            <MenuItem key="select-day" value="">
                              Select Day
                            </MenuItem>
                            {uniqueConDays.map((name: string) => (
                              <MenuItem key={name} value={name}>
                                {name}
                              </MenuItem>
                            ))}
                            </StyledSelect>
                        </Box>
                    </Box>
                  }
              />
            </FormGroup>
          </Grid>
          )
        }

        <Grid size={12}>
          <Typography variant="subtitle1" color="#000000" fontWeight="bold" gutterBottom>
            Other Comments:
          </Typography>
          <StyledTextField
            label=""
            value={otherComments}
            placeholder={setDefaultComment}
            onChange={handleOtherCommentsChange}
            error={validateFields.otherComments.invalid}
            helperText={validateFields.otherComments.error}
            multiline={true}
            inputProps={{ maxLength: 345 }}
          />
          <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
            {otherComments.length}/345 characters
          </Typography>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Grid size={12} sx={{ mt: 4 }}>
          <Button disabled={disabled} variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 25px !important', minWidth: '110px', marginRight: '25px' }} onClick={handleNext}>
            NEXT
          </Button>
          {/* onClick={() => navigate(-1)} */}
          <Button disabled={disabled} variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 25px !important', minWidth: '110px' }} onClick={handleOnBackClick}> 
            BACK
          </Button>
        </Grid>
      </Grid>
    </DashboardLayout>
    </div>
  )
}

export default OnlineOrderEntryHeader